<template>
  <div class="h-screen flex flex-col overflow-hidden">
    <header class="shrink-0 bg-white border-b border-gray-200 z-50">
      <div class="max-w-7xl mx-auto flex items-center h-12 px-4 gap-2">
        <router-link to="/" class="shrink-0">
          <img src="@/favicon.svg" alt="WanderingBook" class="h-7 w-7" />
        </router-link>

        <template v-if="auth.isAuthenticated">
          <nav class="hidden md:flex gap-1 text-sm flex-1">
            <router-link to="/collections/1" class="px-3 py-2 rounded hover:bg-gray-100 whitespace-nowrap" :class="{ 'bg-gray-100 font-medium': isActive('collections') }">{{ $t('app.nav.collections') }}</router-link>
            <router-link to="/books/1/1" class="px-3 py-2 rounded hover:bg-gray-100 whitespace-nowrap" :class="{ 'bg-gray-100 font-medium': isActive('books') }">{{ $t('app.nav.books') }}</router-link>
            <router-link to="/favorites/1" class="px-3 py-2 rounded hover:bg-gray-100 whitespace-nowrap" :class="{ 'bg-gray-100 font-medium': isActive('favorites') }">{{ $t('app.nav.favorites') }}</router-link>
            <router-link v-if="auth.isAdmin" to="/tasks/1" class="px-3 py-2 rounded hover:bg-gray-100 whitespace-nowrap" :class="{ 'bg-gray-100 font-medium': isActive('tasks') }">{{ $t('app.nav.tasks') }}</router-link>
            <router-link to="/dict/genres/1" class="px-3 py-2 rounded hover:bg-gray-100 whitespace-nowrap" :class="{ 'bg-gray-100 font-medium': isActive('dict') }">{{ $t('app.nav.dictionaries') }}</router-link>
          </nav>

          <div class="flex items-center gap-1 text-xs ml-auto md:ml-0">
            <button
              v-for="lang in langs"
              :key="lang.locale"
              @click="setLocale(lang.locale)"
              :class="[
                'px-1.5 py-0.5 rounded',
                locale === lang.locale ? 'bg-green-700 text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
              ]"
            >{{ lang.name }}</button>
          </div>

          <div class="hidden md:block">
            <n-dropdown trigger="click" :options="userMenuOptions" @select="handleUserMenu">
              <n-button text style="font-size:14px">
                {{ auth.user?.full_name || auth.user?.username || $t('app.nav.user') }}
              </n-button>
            </n-dropdown>
          </div>

          <button class="md:hidden text-xl leading-none px-2 py-1 select-none" @click="mobileMenuOpen = !mobileMenuOpen">
            {{ mobileMenuOpen ? '✕' : '☰' }}
          </button>
        </template>
      </div>

      <div v-if="mobileMenuOpen && auth.isAuthenticated" class="md:hidden border-t border-gray-100 bg-white">
        <nav class="flex flex-col text-sm px-4 py-1">
          <router-link to="/collections/1" class="px-3 py-2.5 rounded hover:bg-gray-100" :class="{ 'bg-gray-100 font-medium': isActive('collections') }" @click="mobileMenuOpen = false">{{ $t('app.nav.collections') }}</router-link>
          <router-link to="/books/1/1" class="px-3 py-2.5 rounded hover:bg-gray-100" :class="{ 'bg-gray-100 font-medium': isActive('books') }" @click="mobileMenuOpen = false">{{ $t('app.nav.books') }}</router-link>
          <router-link to="/favorites/1" class="px-3 py-2.5 rounded hover:bg-gray-100" :class="{ 'bg-gray-100 font-medium': isActive('favorites') }" @click="mobileMenuOpen = false">{{ $t('app.nav.favorites') }}</router-link>
          <router-link v-if="auth.isAdmin" to="/tasks/1" class="px-3 py-2.5 rounded hover:bg-gray-100" :class="{ 'bg-gray-100 font-medium': isActive('tasks') }" @click="mobileMenuOpen = false">{{ $t('app.nav.tasks') }}</router-link>
          <router-link to="/dict/genres/1" class="px-3 py-2.5 rounded hover:bg-gray-100" :class="{ 'bg-gray-100 font-medium': isActive('dict') }" @click="mobileMenuOpen = false">{{ $t('app.nav.dictionaries') }}</router-link>
          <div class="border-t my-1"></div>
          <div class="px-3 py-2 text-sm text-gray-400">{{ auth.user?.full_name || auth.user?.username || $t('app.nav.user') }}</div>
          <button class="text-left px-3 py-2.5 rounded hover:bg-gray-100 text-sm" @click="router.push('/profile'); mobileMenuOpen = false">{{ $t('app.nav.profile') }}</button>
          <button class="text-left px-3 py-2.5 rounded hover:bg-gray-100 text-sm" @click="router.push('/recent-books/1'); mobileMenuOpen = false">{{ $t('app.nav.recentBooks') }}</button>
          <button v-if="auth.isAdmin" class="text-left px-3 py-2.5 rounded hover:bg-gray-100 text-sm" @click="router.push('/admin/users/1'); mobileMenuOpen = false">{{ $t('app.nav.admin') }}</button>
          <button class="text-left px-3 py-2.5 rounded hover:bg-gray-100 text-sm text-red-500" @click="auth.oidcConnected ? auth.oidcLogout() : (auth.logout(), router.push('/login')); mobileMenuOpen = false">{{ $t('app.nav.logout') }}</button>
        </nav>
      </div>
    </header>

    <main class="flex-1 overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 py-3 h-full flex flex-col">
        <n-dialog-provider class="flex-1 min-h-0 flex flex-col">
          <n-message-provider class="flex-1 min-h-0 flex flex-col">
            <router-view />
          </n-message-provider>
        </n-dialog-provider>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { langs, setLocale } from '@/langs'
import { NButton, NDropdown, NDialogProvider, NMessageProvider } from 'naive-ui'

const { t, locale } = useI18n()

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const mobileMenuOpen = ref(false)

onMounted(() => {
  auth.fetchAuthConfig()
})

const userMenuOptions = computed(() => {
  const opts = [
    { key: 'profile', label: t('app.nav.profile') },
    { key: 'recent', label: t('app.nav.recentBooks') },
  ]
  if (auth.isAdmin) {
    opts.push({ key: 'admin', label: t('app.nav.admin') })
  }
  opts.push({ type: 'divider', key: 'd1' })
  opts.push({ key: 'logout', label: t('app.nav.logout') })
  return opts
})

function handleUserMenu(key) {
  switch (key) {
    case 'profile':
      router.push('/profile')
      break
    case 'recent':
      router.push('/recent-books/1')
      break
    case 'admin':
      router.push('/admin/users/1')
      break
    case 'logout':
      if (auth.oidcConnected) {
        auth.oidcLogout()
      } else {
        auth.logout()
        router.push('/login')
      }
      break
  }
}

function isActive(section) {
  const p = route.path
  switch (section) {
    case 'collections': return p.startsWith('/collection')
    case 'books': return p.startsWith('/book') || p.startsWith('/books')
    case 'search': return p.startsWith('/search')
    case 'favorites': return p.startsWith('/favorite')
    case 'tasks': return p.startsWith('/task') || p.startsWith('/add')
    case 'dict': return p.startsWith('/dict')
    default: return false
  }
}
</script>
